// Hideo - script editor: fonts with their variants and the editable text styles, without UI (the Font and Style menus
// are ToolbarMenu.vue with the toolbar store, src/stores/toolbar.ts). Ported with the same results (golden tests: tests/editor-styles.test.ts against
// tests/fixtures/legacy/textstyles.js).
// Data: script.styles = { p|h1|…: { ff, fw, fs, it, ul, c } } ({} = the app's original style, even if the profile has
// its own defaults); profile defaults in data.settings.scriptStyles. Drawn with <style id="scriptStylesCss">.
// Known limit kept for now (docs/backlog.md): the "Text" style is written for the whole page (#scriptBody), so its font
// and colour also reach headings, lists and quotes that have no style of their own.
import { t } from '../../i18n'
import type { ProfileData, Script, StyleKey, TextStyle } from '../../data/types'
import { isHex } from '../../utils/color'
import { blockName } from './blockKinds'

/** Weight names as "{font} Weight" templates (word order may differ by language), filled with tFill */
export const FONT_W: Readonly<Record<number, string>> = { 100: t('{font} Thin'), 200: t('{font} Extra light'), 300: t('{font} Light'), 400: t('{font} Regular'), 500: t('{font} Medium'), 600: t('{font} Semi bold'), 700: t('{font} Bold'), 800: t('{font} Extra bold'), 900: t('{font} Black') }
export const W9 = [100, 200, 300, 400, 500, 600, 700, 800, 900]
export const wFrom = (a: number, b: number): number[] => W9.filter(w => w >= a && w <= b)
/** A font of the menu: [name, weights, sibling families] */
type FontEntry = [string, number[], string[]?]
/** [group name, fonts] */
export const FONT_GROUPS: [string, FontEntry[]][] = [
  [t('Sans serif'), [['Lexend', W9, ['Lexend Deca', 'Lexend Exa', 'Lexend Giga', 'Lexend Mega', 'Lexend Peta', 'Lexend Tera', 'Lexend Zetta']], ['Inter', W9], ['Roboto', W9, ['Roboto Condensed', 'Roboto Slab']], ['Open Sans', wFrom(300, 800)], ['Montserrat', W9, ['Montserrat Alternates']], ['Poppins', W9], ['Nunito', wFrom(200, 900), ['Nunito Sans']], ['Lato', [100, 300, 400, 700, 900]], ['Work Sans', W9], ['DM Sans', W9], ['Raleway', W9], ['Source Sans 3', wFrom(200, 900)]]],
  [t('Serif'), [['Merriweather', [300, 400, 700, 900], ['Merriweather Sans']], ['Lora', wFrom(400, 700)], ['Playfair Display', wFrom(400, 900)], ['Source Serif 4', wFrom(200, 900)], ['Libre Baskerville', [400, 700]], ['EB Garamond', wFrom(400, 800)], ['DM Serif Display', [400]]]],
  [t('Display fonts'), [['Oswald', wFrom(200, 700)], ['Bebas Neue', [400]], ['Anton', [400]], ['Archivo Black', [400], ['Archivo']]]],
  [t('Handwriting'), [['Caveat', wFrom(400, 700)], ['Patrick Hand', [400]]]],
  [t('Monospace'), [['Roboto Mono', wFrom(100, 700)], ['JetBrains Mono', wFrom(100, 800)], ['DM Mono', [300, 400, 500]], ['Source Code Pro', wFrom(200, 900)]]],
  [t('System fonts'), [['Georgia', [400, 700]], ['Times New Roman', [400, 700]], ['Arial', [400, 700]], ['Courier New', [400, 700]]]]
]
/** Weights of the sibling families that don't have all nine */
export const FONT_ALT_W: Readonly<Record<string, number[]>> = { 'Merriweather Sans': wFrom(300, 800), 'Nunito Sans': wFrom(200, 900) }
/** Fonts of the computer, not downloaded */
export const FONT_SYSTEM = ['Georgia', 'Times New Roman', 'Arial', 'Courier New']
/** Weights of a font of the menu (or of a sibling family); [400, 700] for any other */
export const fontWeights = (name: string): number[] => {
  for (const [, list] of FONT_GROUPS) for (const f of list) { if (f[0] === name) return f[1]; if ((f[2] || []).includes(name)) return FONT_ALT_W[name] || W9 }
  return [400, 700]
}
let known: Set<string> | null = null
/** A font of the menu or a sibling family, without case (the names are read once: it is asked at every move of the
 *  selection) */
export const fontKnown = (name: string): boolean =>
  (known ||= new Set(FONT_GROUPS.flatMap(([, list]) => list.flatMap(f => [f[0], ...(f[2] || [])].map(x => x.toLowerCase()))))).has(name.toLowerCase())
/** A font name for CSS: in single quotes, without quotes inside */
export const fontCss = (n: unknown): string => `'${String(n).replace(/'/g, '')}'`

/** The Google Fonts stylesheet of a family: every weight with more than one ("…:wght@100;200…") */
const fontHref = (name: string, ws: number[]): string =>
  'https://fonts.googleapis.com/css2?family=' + name.replace(/ /g, '+') + (ws.length > 1 ? ':wght@' + ws.join(';') : '') + '&display=swap'
/** Stylesheets of every menu font and sibling family, in menu order (the computer's fonts excluded) */
export function fontLinks(): string[] {
  const out: string[] = []
  FONT_GROUPS.forEach(([, list]) => list.forEach(([n, ws, alt]) => {
    if (FONT_SYSTEM.includes(n)) return
    out.push(fontHref(n, ws)); (alt || []).forEach(a => out.push(fontHref(a, FONT_ALT_W[a] || W9)))
  }))
  return out
}
let edFontsLoaded = false
/** Downloads every font from Google the first time the editor opens (one <link> per family) */
export function loadEditorFonts(): void {
  if (edFontsLoaded) return
  edFontsLoaded = true
  fontLinks().forEach(href => { const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = href; document.head.appendChild(l) })
}

// ----- Block styles -----
/** Block types with a style, and their names in the menu */
export const STYLE_KEYS: [StyleKey, string][] = (['p', 'h1', 'h2', 'h3', 'h4', 'blockquote', 'pre'] as const).map(k => [k, blockName(k)])
/** Default look in the menu preview (the real style is in the .script-body CSS) */
export const STYLE_PREVIEW: Readonly<Record<StyleKey, string>> = { p: 'font-size:14px', h1: 'font-size:21px;font-weight:800', h2: 'font-size:18px;font-weight:800', h3: 'font-size:15px;font-weight:700;color:rgb(var(--brand-text))', h4: 'font-size:14px;font-weight:700', blockquote: 'font-size:14px;font-style:italic;color:rgb(var(--g400))', pre: 'font-size:13px;font-family:ui-monospace,Consolas,monospace' }
export type StyleSet = Record<StyleKey, TextStyle | null>

/** The styles in use for a script: its own for each block type (an empty one = the original style), else the profile's
 *  default, else null */
export function stylesFor(d: Pick<ProfileData, 'settings'>, sc: Script | null | undefined): StyleSet {
  const own = (sc && sc.styles) || {}, prof = (d.settings && d.settings.scriptStyles) || {}, out = {} as StyleSet
  STYLE_KEYS.forEach(([k]) => { out[k] = k in own ? (own as Record<string, TextStyle>)[k] : (prof[k] || null) })
  return out
}
/** Only the known values of a style: a menu font, a weight 100–900, a size 8–120 px (rounded), italic, underline, a
 *  #rrggbb colour; null when it is not an object */
export const cleanStyle = (v: unknown): TextStyle | null => {
  if (!v || typeof v !== 'object') return null
  const s = v as Record<string, unknown>, o: TextStyle = {}
  if (s.ff && fontKnown(String(s.ff))) o.ff = String(s.ff)
  if (W9.includes(+(s.fw as number))) o.fw = +(s.fw as number)
  if (+(s.fs as number) >= 8 && +(s.fs as number) <= 120) o.fs = Math.round(+(s.fs as number))
  if (s.it) o.it = true
  if (s.ul) o.ul = true
  if (isHex(String(s.c || ''))) o.c = s.c as string   // String(): like the legacy test on any value
  return o
}
/** CSS declarations of a style ('' for none) */
export const styleCss = (v: unknown): string => {
  const c = cleanStyle(v)
  if (!c) return ''
  return (c.ff ? `font-family:${fontCss(c.ff)};` : '') + (c.fw ? `font-weight:${c.fw};` : '') + (c.fs ? `font-size:${c.fs}px;` : '') + (c.it ? 'font-style:italic;' : '') + (c.ul ? 'text-decoration:underline;' : '') + (c.c ? `color:${c.c};` : '')
}
/** Text of <style id="scriptStylesCss">: one rule per block type with a style ("Text" = the whole page) */
export const stylesCssText = (R: Partial<StyleSet>): string =>
  STYLE_KEYS.map(([k]) => { const c = styleCss(R[k]); return c ? `${k === 'p' ? '#scriptBody' : '#scriptBody ' + k}{${c}}` : '' }).join('\n')
/** "Back to the original styles": every block type with an empty style of its own */
export const originalStyles = (): Record<StyleKey, TextStyle> => Object.fromEntries(STYLE_KEYS.map(([k]) => [k, {}])) as Record<StyleKey, TextStyle>
/** "Save as my default styles": the styles in use, cleaned, without the empty ones */
export const defaultStylesOf = (R: Partial<StyleSet>): Partial<Record<StyleKey, TextStyle>> =>
  Object.fromEntries(STYLE_KEYS.map(([k]) => [k, cleanStyle(R[k])] as const).filter(([, v]) => v && Object.keys(v).length))
